import { useState } from "react";
import { Button, DropDown, Loader, Modal } from "@src/components/atoms";
import AdminHooks from "@src/context/hooks/adminHooks";
import { useAuthContext } from "@src/context";

const EMPTY_TYPE = {
  name: "",
  acronym: "",
  behaviourName: "",
  eventTypeId: "",
  groupId: "",
};

const EVENT_TYPE_OPTIONS = [
  { value: "", text: "— none —" },
  { value: "FLIGHT", text: "FLIGHT" },
  { value: "SHIPPING", text: "SHIPPING" },
];

const TypeForm = ({
  initial = EMPTY_TYPE,
  groups = [],
  onSave,
  onCancel,
  isNew,
}) => {
  const [form, setForm] = useState({ ...EMPTY_TYPE, ...initial });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const groupOptions = [
    { value: "", text: "— no group —" },
    ...groups.map((g) => ({ value: g.id, text: g.name })),
  ];

  const set = (key) => (e) =>
    setForm((prev) => ({
      ...prev,
      [key]: typeof e === "object" && e?.target ? e.target.value : e,
    }));

  const handleSave = async () => {
    if (
      !form.name ||
      !form.acronym ||
      !form.behaviourName ||
      !form.eventTypeId
    ) {
      setError("Name, acronym, behaviourName, and eventTypeId are required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await onSave({
        ...form,
        groupId: form.groupId || null,
      });
    } catch {
      setError("Failed to save.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 p-4">
      <h4 className="font-bold text-primary-500 uppercase">
        {isNew ? "New Behaviour Type" : "Edit Behaviour Type"}
      </h4>
      {!isNew && <div className="text-xs text-gray-400">id: {initial.id}</div>}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500">Name *</label>
          <input
            className="w-full border border-neutral-200 rounded p-2 text-sm"
            value={form.name}
            onChange={set("name")}
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Acronym *</label>
          <input
            className="w-full border border-neutral-200 rounded p-2 text-sm"
            value={form.acronym}
            onChange={set("acronym")}
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Behaviour Name *</label>
          <input
            className="w-full border border-neutral-200 rounded p-2 text-sm"
            value={form.behaviourName}
            onChange={set("behaviourName")}
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Event Type ID *</label>
          <input
            className="w-full border border-neutral-200 rounded p-2 text-sm"
            placeholder="UUID of the event type"
            value={form.eventTypeId}
            onChange={set("eventTypeId")}
          />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-gray-500">Group</label>
          <DropDown
            label="Group"
            options={groupOptions}
            value={form.groupId || ""}
            onChange={set("groupId")}
          />
        </div>
      </div>
      {error && <div className="text-xs text-red-500">{error}</div>}
      <div className="flex gap-2 pt-2">
        <Button submit={false} loading={submitting} onClick={handleSave}>
          Save
        </Button>
        <Button secondary onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export const BehaviourTypesSection = () => {
  const auth = useAuthContext();
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const {
    data: behaviourTypes = [],
    isLoading,
    isError,
  } = AdminHooks.useGetBehaviourTypes(auth.activeRole);
  const { data: groups = [] } = AdminHooks.useGetBehaviourGroups(
    auth.activeRole,
  );

  const createMutation = AdminHooks.useCreateBehaviourType();
  const updateMutation = AdminHooks.useUpdateBehaviourType();
  const deleteMutation = AdminHooks.useDeleteBehaviourType();

  const groupName = (groupId) =>
    groups.find((g) => g.id === groupId)?.name ?? "—";

  const handleDelete = async (bt) => {
    if (
      !window.confirm(
        `Delete behaviour type "${bt.name}" (${bt.acronym})? This cannot be undone.`,
      )
    )
      return;
    setError("");
    setSubmitted(false);
    try {
      await deleteMutation.mutateAsync(bt.id);
      setSubmitted(true);
    } catch {
      setError("Failed to delete.");
    }
  };

  const handleSave = async (data) => {
    setError("");
    setSubmitted(false);
    if (adding) {
      await createMutation.mutateAsync(data);
    } else {
      await updateMutation.mutateAsync(data);
    }
    setAdding(false);
    setEditing(null);
    setSubmitted(true);
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between pb-3">
        <h3 className="font-bold text-lg">Global Behaviour Types</h3>
        <Button
          icon="plus"
          block={false}
          onClick={() => setAdding(true)}
          secondary
        >
          Add type
        </Button>
      </div>

      {!!submitted && (
        <div className="mb-2 text-green-600 border border-green-300 rounded-xl p-2 bg-green-50 text-sm">
          Changes saved successfully.
        </div>
      )}
      {!!error && (
        <div className="mb-2 text-red-600 border border-red-300 rounded-xl p-2 bg-red-50 text-sm">
          {error}
        </div>
      )}

      {isLoading && <Loader size="2xl" />}
      {!isLoading && isError && (
        <div className="text-center text-red-500">
          Error loading behaviour types.
        </div>
      )}

      {!isLoading && !isError && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-xs text-gray-500 uppercase">
                <th className="py-2 pr-4">Acronym</th>
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Behaviour Name</th>
                <th className="py-2 pr-4">Event Type ID</th>
                <th className="py-2 pr-4">Group</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {behaviourTypes.map((bt) => (
                <tr
                  key={bt.id}
                  className="border-b border-neutral-100 hover:bg-neutral-50"
                >
                  <td className="py-2 pr-4 font-bold">{bt.acronym}</td>
                  <td className="py-2 pr-4">{bt.name}</td>
                  <td className="py-2 pr-4">{bt.behaviourName}</td>
                  <td className="py-2 pr-4 text-xs text-gray-400 font-mono">
                    {bt.eventTypeId}
                  </td>
                  <td className="py-2 pr-4">{groupName(bt.groupId)}</td>
                  <td className="py-2">
                    <div className="flex gap-1">
                      <Button
                        icon="feedback"
                        block={false}
                        secondary
                        onClick={() => setEditing(bt)}
                      >
                        Edit
                      </Button>
                      <Button
                        icon="close"
                        color="red"
                        block={false}
                        secondary
                        onClick={() => handleDelete(bt)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {behaviourTypes.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-gray-400">
                    No behaviour types defined.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(editing || adding) && (
        <Modal
          open={!!(editing || adding)}
          handleClose={() => {
            setEditing(null);
            setAdding(false);
          }}
        >
          <TypeForm
            initial={editing || EMPTY_TYPE}
            groups={groups}
            isNew={adding}
            onSave={handleSave}
            onCancel={() => {
              setEditing(null);
              setAdding(false);
            }}
          />
        </Modal>
      )}
    </div>
  );
};
